'use client'

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react'
import clsx from 'clsx'
import { formatDate } from 'date-fns'
import CalendarWidget, {
  CalendarWidgetProps,
} from 'lib/components/CalendarWidget/CalendarWidget'
import Icon from 'lib/components/Icon/Icon'
import { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import { NaiveDate } from 'lib/utilities/dateTime'
import {
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Controller } from 'react-hook-form'
import {
  HelpCenterButton,
  HelpCenterButtonRenderProps,
} from '../../HelpCenter/HelpCenter'
import InputAddon, { InputAddonProps } from '../InputAddon/InputAddon'
import InputError from '../InputError/InputError'
import InputHint from '../InputHint/InputHint'
import InputLabel from '../InputLabel/InputLabel'

type DateInputStandaloneProps = Omit<FormFieldCommonProps, 'value'> &
  InputAddonProps & {
    /**
     * An optional label for the input
     */
    label?: string
    /**
     * Optional placeholder text to show when no date is selected yet
     */
    placeholder?: string
    /**
     * Default month to display on the calendar.
     */
    defaultMonth?: Date | string
    /**
     * Optional first eligible date to be selected. Should be in format `YYYY-MM-DD`
     */
    min?: string
    /**
     * Optional last eligible date to be selected. Should be in format `YYYY-MM-DD`
     */
    max?: string
    /**
     * Optional list of dates to exclude from selection.
     * Can either be a list of string in `YYYY-MM-DD` format,
     * or `react-day-picker`'s `Matcher` type
     */
    exclude?: CalendarWidgetProps['excludeDates']
    /**
     * Allows full control on formatting day content
     * If return value is null, default DayContent formatting will take effect
     */
    dayContentFormat?: CalendarWidgetProps['dayContentFormat']
    /**
     * Optional additional classname
     */
    className?: string
    /**
     * Optional onChange event handler
     */
    onChange?: (data: any) => void
    /**
     * Error message
     */
    error?: string
    /**
     * Whether to hide the calendar and dropdown icons
     */
    hideIcons?: boolean
  }

type DateInputProps = DateInputStandaloneProps &
  Omit<ControlledFormField, 'value' | 'label'>

// Standalone component that can be used outside react-hook-form's context
export const DateInputStandalone = forwardRef<
  HTMLInputElement,
  DateInputStandaloneProps
>(function DateInputStandaloneWithRef(
  {
    label,
    name,
    id = name,
    defaultValue,
    defaultMonth,
    min,
    max,
    exclude = [],
    className,
    placeholder,
    onChange,
    hint,
    disabled,
    dayContentFormat,
    error,
    hideIcons,
    ...props
  }: DateInputStandaloneProps,
  ref,
) {
  const [isEnabled, setIsEnabled] = useState(
    'conditionalEdit' in props && props.conditionalEdit ? false : !disabled,
  )
  const [selectedDate, setSelectedDate] = useState(defaultValue)
  const displayedDate = useMemo(() => {
    return selectedDate
      ? formatDate(new NaiveDate(selectedDate), 'MMM d, yyyy')
      : undefined
  }, [selectedDate])

  const onDateSelect = useCallback(
    (date?: string, close?: () => void) => {
      setSelectedDate(date || '')
      onChange?.(date)
      close?.()
    },
    [onChange],
  )

  const handlingMethod = useMemo(() => {
    return label?.toLowerCase().includes('pickup') ? 'pickup' : 'dropoff'
  }, [label])

  useEffect(() => {
    setSelectedDate(defaultValue)
  }, [defaultValue])

  // Whenever the parent’s disabled changes, update our local state
  useEffect(() => {
    // Only force-sync if we aren’t in "conditionalEdit" mode
    if (!('conditionalEdit' in props && props.conditionalEdit)) {
      setIsEnabled(!disabled)
    }
  }, [disabled, props, props.conditionalEdit])

  return (
    <div className={clsx('w-full', className)}>
      <div className="flex items-center justify-between w-full">
        {label && (
          <InputLabel className="w-full" htmlFor={id}>
            {label}
            <HelpCenterButton
              item={handlingMethod}
              render={(buttonProps: HelpCenterButtonRenderProps) => (
                <button
                  type="button"
                  aria-label="Click to view help notes"
                  {...buttonProps}
                >
                  <Icon
                    icon="help-circle-outline"
                    className="ml-2 mt-[-4px] w-4 h-4"
                  />
                </button>
              )}
            />
          </InputLabel>
        )}
        <InputAddon
          {...props}
          {...('conditionalEdit' in props && props.conditionalEdit
            ? { onEdit: setIsEnabled }
            : {})}
        />
      </div>
      {/* <input name={name} type="hidden" value={selectedDate} ref={ref} /> */}
      <Popover className="group">
        <PopoverButton
          type="button"
          disabled={disabled || !isEnabled}
          className={clsx(
            'w-full h-12 inline-flex items-center px-4 bg-white rounded-8 my-2 type-body-8 text-neutral-600 lg:h-14',
            // Hover
            'data-[hover]:shadow-input-active',
            // Focus
            'focus:outline-none focus-visible:outline-none focus:border-theme-secondary',
            // Disabled
            'data-[disabled]:cursor-not-allowed data-[disabled]:bg-neutral-200 data-[disabled]:text-neutral-600',
            // Error
            error ? '!shadow-input-error' : 'shadow-input',
            // Filled state
            displayedDate && 'text-neutral-700',
          )}
          ref={ref as MutableRefObject<HTMLButtonElement>}
        >
          {!hideIcons ? (
            <Icon icon="calendar" className="mr-2 w-6 h-6" />
          ) : null}
          <span className="inline-block truncate">
            {displayedDate || placeholder}
          </span>
          {!hideIcons ? (
            <Icon icon="chevron-down" className="ml-auto w-6 h-6" />
          ) : null}
        </PopoverButton>
        <Transition
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 -translate-y-2"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-2"
        >
          <PopoverPanel
            modal
            anchor="bottom start"
            className="bg-white shadow rounded-8 border border-neutral-400 translate-y-2"
          >
            {({ close }) => (
              <CalendarWidget
                className="!border-0"
                defaultMonth={defaultMonth}
                value={selectedDate as string}
                startDate={min}
                endDate={max}
                excludeDates={exclude}
                onChange={(date) => onDateSelect(date, close)}
                dayContentFormat={dayContentFormat}
              />
            )}
          </PopoverPanel>
        </Transition>
      </Popover>
      <InputHint>{hint}</InputHint>
      <InputError>{error}</InputError>
    </div>
  )
})

// Controlled form element that is automatically registered to react-hook-form
export default function DateInput({
  name,
  control,
  onChange,
  rules,
  ...props
}: DateInputProps) {
  return (
    <Controller
      name={name || ''}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <DateInputStandalone
          {...field}
          {...props}
          error={fieldState.error?.message || ''}
          onChange={(data) => {
            field.onChange?.(data)
            onChange?.(data)
          }}
        />
      )}
    />
  )
}
